import React from "react";
import { Disc3, Ticket, Cherry, Goal, Check, ChevronRight, LucideIcon } from "lucide-react";

import {
  CampaignType,
  CAMPAIGN_TYPE_LABELS,
  CAMPAIGN_TYPE_DESCRIPTIONS,
} from "@/@types/gamification";

const ACCENT = "#f5c518";
const ACCENT_SOFT = "#fdf6dd";
const INK = "#2b0c1a";

const COPY = {
  HEADER_TITLE: "Escolha a mecânica da campanha",
  HEADER_DESCRIPTION: "Selecione o tipo de campanha que deseja criar.",
} as const;

const OPTION_ICONS: Record<CampaignType, LucideIcon> = {
  ROULETTE: Disc3,
  SCRATCH: Ticket,
  SLOT: Cherry,
  PENALTY: Goal,
};

const OPTION_ORDER: CampaignType[] = [
  CampaignType.ROULETTE,
  CampaignType.SCRATCH,
  CampaignType.SLOT,
  CampaignType.PENALTY,
];

interface CampaignTypeSelectorProps {
  value: CampaignType;
  onChange: (value: CampaignType) => void;
  disabled?: boolean;
}

export const CampaignTypeSelector: React.FC<CampaignTypeSelectorProps> = ({
  value,
  onChange,
  disabled,
}) => {
  return (
    <div>
      <p className="text-base font-semibold" style={{ color: INK }}>
        {COPY.HEADER_TITLE}
      </p>
      <p className="text-sm text-gray-500 mb-4">{COPY.HEADER_DESCRIPTION}</p>

      <div className="flex flex-col gap-3">
        {OPTION_ORDER.map((optionValue) => {
          const isSelected = value === optionValue;
          const Icon = OPTION_ICONS[optionValue];
          return (
            <button
              key={optionValue}
              type="button"
              disabled={disabled}
              onClick={() => onChange(optionValue)}
              aria-pressed={isSelected}
              style={
                isSelected
                  ? { borderColor: ACCENT, backgroundColor: ACCENT_SOFT }
                  : undefined
              }
              className={[
                "flex w-full items-center gap-4 rounded-xl border bg-white p-4 text-left transition",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f5c518]",
                isSelected ? "shadow-sm" : "border-gray-200 hover:border-gray-300",
                disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
              ].join(" ")}
            >
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border"
                style={
                  isSelected
                    ? { borderColor: ACCENT, backgroundColor: "#fff" }
                    : { borderColor: "#e5e7eb", backgroundColor: "#fafafa" }
                }
              >
                <Icon className="h-5 w-5" style={{ color: INK }} aria-hidden="true" />
              </span>

              <span className="min-w-0 flex-1">
                <span className="block font-semibold" style={{ color: INK }}>
                  {CAMPAIGN_TYPE_LABELS[optionValue]}
                </span>
                <span className="block truncate text-sm text-gray-500">
                  {CAMPAIGN_TYPE_DESCRIPTIONS[optionValue]}
                </span>
              </span>

              {isSelected ? (
                <span
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: ACCENT }}
                >
                  <Check className="h-4 w-4" style={{ color: INK }} aria-hidden="true" />
                </span>
              ) : (
                <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
